import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Code2,
  Cpu,
  Layers,
  Terminal,
  Wrench,
  CheckCircle2,
} from "lucide-react";
import { JsonViewer } from "./JsonViewer";

export interface WaterfallEvent {
  id?: string;
  step_number: number;
  event_type: string;
  payload: Record<string, any>;
  created_at?: string;
}

interface EventWaterfallProps {
  events: WaterfallEvent[];
}

export function EventWaterfall({ events }: EventWaterfallProps) {
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});

  const toggleExpand = (stepNumber: number) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepNumber]: !prev[stepNumber],
    }));
  };

  const expandAll = () => {
    const all: Record<number, boolean> = {};
    events.forEach((e) => (all[e.step_number] = true));
    setExpandedSteps(all);
  };

  const collapseAll = () => {
    setExpandedSteps({});
  };

  const getEventBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case "model_request":
      case "prompt_assembled":
        return {
          icon: Layers,
          label: "Model Request",
          color: "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800",
          dotColor: "bg-indigo-500",
        };
      case "model_response":
      case "llm_completion":
        return {
          icon: Cpu,
          label: "Model Response",
          color: "bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800",
          dotColor: "bg-purple-500",
        };
      case "tool_call":
        return {
          icon: Wrench,
          label: "Tool Call",
          color: "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
          dotColor: "bg-amber-500",
        };
      case "tool_output":
        return {
          icon: Terminal,
          label: "Tool Output",
          color: "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
          dotColor: "bg-emerald-500",
        };
      case "output_generation_completed":
      case "completed":
        return {
          icon: CheckCircle2,
          label: "Output Completed",
          color: "bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800",
          dotColor: "bg-teal-500",
        };
      default:
        return {
          icon: Code2,
          label: type.replace(/_/g, " "),
          color: "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700",
          dotColor: "bg-stone-500",
        };
    }
  };

  const renderEventSummary = (event: WaterfallEvent) => {
    const p = event.payload || {};
    switch (event.event_type.toLowerCase()) {
      case "model_request":
      case "prompt_assembled":
        return (
          <span className="text-xs text-stone-600 dark:text-stone-400">
            Request sent to <span className="font-semibold text-stone-900 dark:text-stone-200">{p.model || "LLM"}</span>
            {p.message_count !== undefined && (
              <span className="ml-1 text-stone-500">
                ({p.message_count} {p.message_count === 1 ? "message" : "messages"})
              </span>
            )}
            {p.active_rules_count !== undefined && p.active_rules_count > 0 && (
              <span className="ml-1 px-1.5 py-0.2 rounded bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 font-mono text-[10px]">
                {p.active_rules_count} rules
              </span>
            )}
          </span>
        );
      case "model_response":
      case "llm_completion": {
        const providerName =
          p.provider ||
          (p.model
            ? String(p.model).includes("/") || String(p.model).startsWith("openrouter:")
              ? "OpenRouter"
              : String(p.model).toLowerCase().startsWith("gemini-")
              ? "Google"
              : undefined
            : undefined);

        const toolCalls: string[] = p.tool_calls_requested || [];
        const hasToolCall = toolCalls.length > 0;

        return (
          <span className="text-xs text-stone-600 dark:text-stone-400">
            Response from <span className="font-semibold text-stone-900 dark:text-stone-200">{p.model || "LLM"}</span>
            {providerName && (
              <span className="ml-1 px-1 py-0.2 rounded bg-amber-50/80 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 font-mono text-[9.5px] border border-amber-200/60 dark:border-amber-800/60">
                {providerName}
              </span>
            )}
            {p.latency_ms !== undefined && (
              <span className="ml-1.5 font-mono text-purple-700 dark:text-purple-300 font-semibold">
                {p.latency_ms >= 1000 ? `${(p.latency_ms / 1000).toFixed(2)}s` : `${p.latency_ms}ms`}
              </span>
            )}
            {p.tokens_in || p.tokens_out ? (
              <span className="font-mono text-stone-500 dark:text-stone-400 ml-1">
                ({Number(p.tokens_in || 0).toLocaleString()} in / {Number(p.tokens_out || 0).toLocaleString()} out{p.cached_tokens ? `, ${Number(p.cached_tokens).toLocaleString()} cached` : ""})
              </span>
            ) : ""}
            {hasToolCall ? (
              <span className="ml-1.5 inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium">
                &rarr; requested tool: <code className="bg-amber-100/70 dark:bg-amber-950/70 px-1 py-0.5 rounded text-[11px] font-mono">{toolCalls.join(", ")}</code>
              </span>
            ) : p.decision ? (
              <span className="ml-1.5 text-stone-700 dark:text-stone-300">&rarr; {p.decision}</span>
            ) : null}
          </span>
        );
      }
      case "tool_call":
        return (
          <span className="text-xs text-stone-600 dark:text-stone-400">
            Tool invoked: <code className="font-semibold text-amber-900 dark:text-amber-100 bg-amber-100/80 dark:bg-amber-950/80 px-1 py-0.5 rounded font-mono">{p.tool_name || p.tool}</code>
            {p.arguments ? ` with ${Object.keys(p.arguments).length} arguments` : ""}
          </span>
        );
      case "tool_output":
        return (
          <span className="text-xs text-stone-600 dark:text-stone-400">
            Tool completed: <code className="font-semibold text-stone-900 dark:text-stone-100 font-mono">{p.tool_name}</code>
            {p.latency_ms !== undefined && (
              <span className="ml-1.5 font-mono text-emerald-700 dark:text-emerald-300 font-semibold">
                {p.latency_ms}ms
              </span>
            )}
            {" — "}
            {p.available !== undefined ? (
              p.available ? (
                <span className="text-emerald-700 dark:text-emerald-400 font-medium">available (rates found)</span>
              ) : (
                <span className="text-rose-700 dark:text-rose-400 font-medium">unavailable</span>
              )
            ) : p.hits ? (
              <span>{p.hits} search matches</span>
            ) : p.status ? (
              <span className="capitalize">{p.status}</span>
            ) : (
              <span>payload returned</span>
            )}
          </span>
        );
      case "output_generation_completed":
        return (
          <span className="text-xs text-stone-600 dark:text-stone-400">
            Run finished{" "}
            {p.tokens_out ? `(${p.tokens_out} tokens, reason: ${p.finish_reason || "stop"})` : ""}
          </span>
        );
      default:
        return <span className="text-xs text-stone-600 dark:text-stone-400">{JSON.stringify(p).slice(0, 80)}...</span>;
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between pb-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100 tracking-tight">Step Event Waterfall</h3>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300">
            {events.length} Steps
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={expandAll}
            className="text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition"
          >
            Expand All
          </button>
          <span className="text-stone-300 dark:text-stone-600">|</span>
          <button
            type="button"
            onClick={collapseAll}
            className="text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition"
          >
            Collapse All
          </button>
        </div>
      </div>

      <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-stone-200 dark:before:bg-stone-800">
        {events.map((event) => {
          const badge = getEventBadge(event.event_type);
          const Icon = badge.icon;
          const isExpanded = !!expandedSteps[event.step_number];

          return (
            <div key={event.step_number} className="relative group">
              {/* Timeline Connector Dot */}
              <div
                className={`absolute -left-6 top-2.5 w-3 h-3 rounded-full border-2 border-white dark:border-stone-950 shadow-xs ${badge.dotColor}`}
              />

              {/* Event Step Card */}
              <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-2xs hover:border-stone-300 dark:hover:border-stone-700 transition overflow-hidden">
                <div
                  onClick={() => toggleExpand(event.step_number)}
                  className="p-3.5 flex items-center justify-between cursor-pointer select-none bg-stone-50/40 dark:bg-stone-800/40 hover:bg-stone-50/80 dark:hover:bg-stone-800/80 transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-md bg-stone-900 dark:bg-stone-800 text-white font-mono text-[10px] font-bold flex items-center justify-center shrink-0">
                      {event.step_number}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold border ${badge.color}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{badge.label}</span>
                    </span>
                    <div className="truncate min-w-0">{renderEventSummary(event)}</div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    {event.created_at && (
                      <span className="text-[11px] text-stone-400 dark:text-stone-500 font-mono">
                        {new Date(event.created_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })}
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-stone-400 dark:text-stone-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-stone-400 dark:text-stone-500" />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-3.5 border-t border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-3">
                    {Boolean(event.payload?.raw_request) && (
                      <div className="rounded-xl border border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50/30 dark:bg-indigo-950/20 p-3">
                        <div className="flex items-center justify-between pb-2 mb-2 border-b border-indigo-100 dark:border-indigo-900/50">
                          <span className="text-xs font-bold text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5 font-mono">
                            <Layers className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                            <span>Raw HTTP Wire Request (OpenRouter / Model API)</span>
                          </span>
                          <span className="text-[10px] uppercase font-mono px-1.5 py-0.2 rounded bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 font-semibold">
                            Exact Wire Format
                          </span>
                        </div>
                        <JsonViewer
                          title="Raw Wire Body"
                          data={event.payload.raw_request}
                          maxHeight="max-h-96"
                          defaultMode="tree"
                        />
                      </div>
                    )}
                    <JsonViewer
                      title={event.payload?.raw_request ? "Agent Diagnostic Context" : "Raw Event Payload"}
                      data={event.payload}
                      maxHeight="max-h-80"
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
